import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, UseMutationResult, useQueryClient } from 'react-query';
import { getSourceBrandLocationsEndpointIdentifier } from './use-get-source-brand-locations-api';

const endpoint = 'ums/v1/crm/management/source-brand-locations';
export const setMasterSourceBrandLocationEndpointIdentifier = endpoint;

interface MutationPayload {
  id: number;
  isMaster: boolean;
}

type TData = {
  message: string;
};
type TError = AxiosError<{ message: string }>;
type TContext = unknown;

const useSetMasterSourceBrandLocationApi = (
  options?: UseMutationOptions<TData, TError, MutationPayload, TContext>
): UseMutationResult<TData, TError, MutationPayload, TContext> => {
  const toast = useCustomToast();
  const queryClient = useQueryClient();

  return useMutationWrapper<TData, TError, MutationPayload, TContext>(
    async ({ id, isMaster }: MutationPayload) => {
      const response = await http.patch(`${endpoint}/${id}/master`, { isMaster });
      return response.data;
    },
    {
      onSuccess: (_, variables) => {
        toast({
          title: 'Success',
          description: `Location ${variables.isMaster ? 'set as master' : 'removed as master'} successfully`,
          status: 'success',
        });

        // Invalidate main list queries
        queryClient.invalidateQueries({
          predicate: (query) => {
            const [queryEndpoint, params] = query.queryKey;
            return (
              queryEndpoint === getSourceBrandLocationsEndpointIdentifier &&
              typeof params === 'object' &&
              params !== null &&
              !Array.isArray(params)
            );
          },
        });

        // Invalidate specific location details
        queryClient.invalidateQueries([getSourceBrandLocationsEndpointIdentifier, 'details', variables.id]);
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error.response?.data?.message || 'Failed to update master status',
          status: 'error',
        });
      },
      ...options,
    }
  );
};

export { useSetMasterSourceBrandLocationApi };
